/**
 * IT Admin → Routing register (16 Oct): who should own which type of ticket.
 *
 * Rules (ticket type → team, lead and backups, with what still needs work),
 * proposals waiting for IT management (dispatchers' decisions), the
 * first-level dispatch group (who is away, who is next), and the change
 * history. Every IT user reads it; IT management edits (`can_edit`).
 */

import { Alert, Badge, Button, Field, Input, Tabs, Typography } from "@norican/react";
import { useState } from "react";

import { ErrorState, LoadingState, SectionCard, useDateFormatter } from "@/components/common";
import { useT } from "@/i18n";
import type { RoutingRegisterResponse, RoutingRule } from "@/lib/api";

import {
  useAddDispatcher,
  useAutoRouteTest,
  useLearnNow,
  useRemoveDispatcher,
  useRoutingAudit,
  useRoutingRegister,
  useRuleAction,
  useSetAway,
} from "../assign/api";
import { ruleMatchesFilter, ruleMatchSummary } from "../assign/lib";
import { RuleDialog } from "./rule-dialog";

type Tab = "rules" | "proposed" | "dispatch" | "history";

function RuleCard({
  rule,
  canEdit,
  onEdit,
}: {
  rule: RoutingRule;
  canEdit: boolean;
  onEdit: (rule: RoutingRule) => void;
}) {
  const { t } = useT("tickets");
  const { formatDate } = useDateFormatter();
  const act = useRuleAction();
  const actions: ("accept" | "reject" | "confirm" | "retire" | "restore")[] =
    rule.status === "proposed"
      ? ["accept", "reject"]
      : rule.status === "retired"
        ? ["restore"]
        : rule.confirmed
          ? ["retire"]
          : ["confirm", "retire"];
  return (
    <div className="border-border flex flex-col gap-1 border-b py-3 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <Typography variant="label">{rule.label}</Typography>
        <Badge size="sm" variant="outline">
          {t(`itAdmin.routing.kind.${rule.kind}`)}
        </Badge>
        {rule.status === "active" ? null : (
          <Badge size="sm" tone={rule.status === "proposed" ? "warning" : "neutral"}>
            {t(`itAdmin.routing.status.${rule.status}`)}
          </Badge>
        )}
        {(rule.gaps ?? []).map((gap) => (
          <Badge key={gap} size="sm" tone={gap === "no_owner" ? "danger" : "caution"}>
            {t(`itAdmin.routing.gap.${gap as "no_owner"}`)}
          </Badge>
        ))}
      </div>
      <Typography variant="body-sm">
        <strong>{rule.team}</strong>
        {(rule.owners ?? []).length > 0
          ? ` · ${t("itAdmin.routing.lead")}: ${(rule.owners ?? []).map((p) => p.name).join(", ")}`
          : ""}
        {(rule.backups ?? []).length > 0
          ? ` · ${t("itAdmin.routing.backups")}: ${(rule.backups ?? []).map((p) => p.name).join(", ")}`
          : ""}
      </Typography>
      <Typography variant="caption" tone="muted">
        {t("itAdmin.routing.matches")}: {ruleMatchSummary(rule) || "—"} ·{" "}
        {t(`itAdmin.routing.source.${rule.source as "guide"}`)} ·{" "}
        {t("itAdmin.routing.updated", {
          when: formatDate(rule.updated_at),
          who: rule.updated_by,
        })}
      </Typography>
      {rule.note ? (
        <Typography variant="caption" tone="muted" className="whitespace-pre-line">
          {rule.note}
        </Typography>
      ) : null}
      {canEdit ? (
        <div className="flex flex-wrap gap-2 pt-1">
          <Button size="sm" variant="outline" onClick={() => onEdit(rule)}>
            {t("itAdmin.routing.edit")}
          </Button>
          {actions.map((action) => (
            <Button
              key={action}
              size="sm"
              variant="outline"
              tone={action === "reject" || action === "retire" ? "danger" : "neutral"}
              loading={act.isPending && act.variables.body.action === action}
              onClick={() => act.mutate({ id: rule.id, body: { action } })}
            >
              {t(`itAdmin.routing.action.${action}`)}
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function SettingsStrip({ data }: { data: RoutingRegisterResponse }) {
  const { t } = useT("tickets");
  const { formatDateTime } = useDateFormatter();
  const learn = useLearnNow();
  const s = data.settings;
  const onOff = (on: boolean) =>
    on ? t("itAdmin.routing.settings.on") : t("itAdmin.routing.settings.off");
  return (
    <div className="flex flex-col gap-1">
      <Typography variant="body-sm">
        {t("itAdmin.routing.settings.decisions")}:{" "}
        {t(`itAdmin.routing.settings.mode.${s.decision_mode}`)} ·{" "}
        {t("itAdmin.routing.settings.threshold", { pct: String(s.decision_threshold) })} ·{" "}
        {t("itAdmin.routing.settings.ai")}: {onOff(s.ai_enabled)} ·{" "}
        {t("itAdmin.routing.settings.autoAssign")}: {onOff(s.auto_assign_enabled)} ·{" "}
        {t("itAdmin.routing.settings.roundRobin")}: {onOff(s.round_robin_enabled)}
      </Typography>
      <div className="flex flex-wrap items-center gap-2">
        <Typography variant="caption" tone="muted">
          {s.outcomes_learned_at
            ? t("itAdmin.routing.settings.learned", {
                count: String(s.outcomes_count),
                when: formatDateTime(s.outcomes_learned_at),
              })
            : t("itAdmin.routing.settings.notLearned")}{" "}
          · {t("itAdmin.routing.settings.editors", { teams: (s.editor_teams ?? []).join(", ") })}
        </Typography>
        {data.can_edit ? (
          <Button
            size="sm"
            variant="ghost"
            loading={learn.isPending}
            onClick={() => learn.mutate()}
          >
            {t("itAdmin.routing.settings.learnNow")}
          </Button>
        ) : null}
      </div>
      {learn.error == null ? null : <ErrorState error={learn.error} variant="inline" />}
    </div>
  );
}

function DispatchTab({ data }: { data: RoutingRegisterResponse }) {
  const { t } = useT("tickets");
  const { formatDate } = useDateFormatter();
  const setAway = useSetAway();
  const remove = useRemoveDispatcher();
  const add = useAddDispatcher();
  const test = useAutoRouteTest();
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const result = test.data;
  return (
    <div className="flex flex-col gap-6">
      <SectionCard title={t("itAdmin.routing.dispatch.title")}>
        <div className="flex flex-col gap-3">
          <Typography variant="body-sm" tone="muted">
            {t("itAdmin.routing.dispatch.intro", { team: data.settings.round_robin_team })}
          </Typography>
          <Typography variant="label">
            {data.next_dispatcher
              ? t("itAdmin.routing.dispatch.next", { name: data.next_dispatcher })
              : t("itAdmin.routing.dispatch.nobody")}
          </Typography>
          {[setAway.error, remove.error, add.error].map((error, i) =>
            error == null ? null : <ErrorState key={String(i)} error={error} variant="inline" />,
          )}
          {(data.dispatchers ?? []).map((d) => (
            <div key={d.id} className="flex flex-wrap items-center gap-2">
              <Typography variant="body-sm">{d.name}</Typography>
              {d.email ? null : (
                <Badge size="sm" tone="caution">
                  {t("itAdmin.routing.dispatch.notInItsm")}
                </Badge>
              )}
              {d.away ? (
                <Badge size="sm" tone="warning">
                  {d.away_until
                    ? t("itAdmin.routing.dispatch.awayUntil", { date: formatDate(d.away_until) })
                    : t("itAdmin.routing.dispatch.away")}
                </Badge>
              ) : null}
              {data.can_edit ? (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setAway.mutate({ id: d.id, body: { away: !d.away } })}
                  >
                    {d.away
                      ? t("itAdmin.routing.dispatch.back")
                      : t("itAdmin.routing.dispatch.setAway")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    tone="danger"
                    onClick={() => remove.mutate(d.id)}
                  >
                    {t("itAdmin.routing.dispatch.remove")}
                  </Button>
                </>
              ) : null}
            </div>
          ))}
          {data.can_edit ? (
            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                if (name.trim() !== "") {
                  add.mutate({ name: name.trim() }, { onSuccess: () => setName("") });
                }
              }}
            >
              <Field name="dispatcher-name" label={t("itAdmin.routing.dispatch.name")}>
                <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={255} />
              </Field>
              <Button type="submit" variant="outline" loading={add.isPending}>
                {t("itAdmin.routing.dispatch.add")}
              </Button>
            </form>
          ) : null}
        </div>
      </SectionCard>
      {data.can_edit ? (
        <SectionCard title={t("itAdmin.routing.test.title")}>
          <div className="flex flex-col gap-3">
            <Typography variant="body-sm" tone="muted">
              {t("itAdmin.routing.test.hint")}
            </Typography>
            <Field name="auto-route-number" label={t("itAdmin.routing.test.number")}>
              <Input value={number} onChange={(e) => setNumber(e.target.value)} maxLength={32} />
            </Field>
            <div className="flex flex-wrap gap-2">
              {(["auto_assign", "round_robin"] as const).map((mode) => (
                <Button
                  key={mode}
                  variant="outline"
                  disabled={number.trim() === ""}
                  loading={test.isPending && test.variables.mode === mode}
                  onClick={() => test.mutate({ ticket_number: number.trim(), mode })}
                >
                  {mode === "auto_assign"
                    ? t("itAdmin.routing.test.autoAssign")
                    : t("itAdmin.routing.test.roundRobin")}
                </Button>
              ))}
            </div>
            {test.error == null ? null : <ErrorState error={test.error} variant="inline" />}
            {result ? (
              <Alert tone={result.applied ? "success" : "neutral"}>
                {result.applied
                  ? t("itAdmin.routing.test.applied", {
                      team: result.team ?? "",
                      owner: result.owner ?? "",
                    })
                  : t("itAdmin.routing.test.notApplied", { detail: result.detail })}
              </Alert>
            ) : null}
          </div>
        </SectionCard>
      ) : null}
    </div>
  );
}

function HistoryTab() {
  const { t } = useT("tickets");
  const { formatDateTime } = useDateFormatter();
  const audit = useRoutingAudit(true);
  if (audit.isPending) {
    return <LoadingState />;
  }
  if (audit.error != null) {
    return <ErrorState error={audit.error} variant="inline" />;
  }
  const items = audit.data.items ?? [];
  if (items.length === 0) {
    return <Typography variant="body-sm">{t("itAdmin.routing.history.empty")}</Typography>;
  }
  return (
    <div className="flex flex-col">
      {items.map((entry) => (
        <div key={entry.id} className="border-border border-b py-2 last:border-b-0">
          <Typography variant="body-sm">
            <strong>{t(`itAdmin.routing.history.action.${entry.action as "add"}`)}</strong>
            {entry.rule_id ? ` · #${String(entry.rule_id)}` : ""} · {entry.actor} ·{" "}
            {formatDateTime(entry.created_at)}
          </Typography>
          {entry.reason ? (
            <Typography variant="caption" tone="muted">
              {entry.reason}
            </Typography>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export function RoutingPage() {
  const { t } = useT("tickets");
  const register = useRoutingRegister();
  const [tab, setTab] = useState<Tab>("rules");
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState<RoutingRule | null>(null);
  const [open, setOpen] = useState(false);

  if (register.isPending) {
    return <LoadingState />;
  }
  if (register.error != null) {
    return <ErrorState error={register.error} />;
  }
  const data = register.data;
  const all = data.rules ?? [];
  const canEdit = data.can_edit === true;
  const rules = all.filter((r) => r.status !== "proposed" && ruleMatchesFilter(r, filter));
  const proposed = all.filter((r) => r.status === "proposed");
  const edit = (rule: RoutingRule | null) => {
    setEditing(rule);
    setOpen(true);
  };

  return (
    <div className="flex flex-col gap-4">
      <Typography variant="body-sm" tone="muted">
        {t("itAdmin.routing.intro")}
      </Typography>
      {data.can_edit ? null : <Alert tone="neutral">{t("itAdmin.routing.readOnly")}</Alert>}
      <SettingsStrip data={data} />
      <Tabs.Root value={tab} onValueChange={(value) => setTab(value as Tab)}>
        <Tabs.List aria-label={t("itAdmin.section.routing")}>
          <Tabs.Tab value="rules">{t("itAdmin.routing.tabs.rules")}</Tabs.Tab>
          <Tabs.Tab value="proposed">
            {t("itAdmin.routing.tabs.proposed")}
            {proposed.length > 0 ? ` (${String(proposed.length)})` : ""}
          </Tabs.Tab>
          <Tabs.Tab value="dispatch">{t("itAdmin.routing.tabs.dispatch")}</Tabs.Tab>
          <Tabs.Tab value="history">{t("itAdmin.routing.tabs.history")}</Tabs.Tab>
          <Tabs.Indicator />
        </Tabs.List>
        <Tabs.Panel value="rules">
          <SectionCard padding="md" className="mt-4">
            <div className="flex flex-wrap items-end justify-between gap-2 pb-2">
              <Field name="routing-filter" label={t("itAdmin.routing.filter")}>
                <Input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder={t("itAdmin.routing.filterPlaceholder")}
                />
              </Field>
              {data.can_edit ? (
                <Button onClick={() => edit(null)}>{t("itAdmin.routing.add")}</Button>
              ) : null}
            </div>
            {rules.length === 0 ? (
              <Typography variant="body-sm">{t("itAdmin.routing.empty")}</Typography>
            ) : (
              rules.map((rule) => (
                <RuleCard key={rule.id} rule={rule} canEdit={canEdit} onEdit={edit} />
              ))
            )}
          </SectionCard>
        </Tabs.Panel>
        <Tabs.Panel value="proposed">
          <SectionCard padding="md" className="mt-4">
            {proposed.length === 0 ? (
              <Typography variant="body-sm">{t("itAdmin.routing.proposedEmpty")}</Typography>
            ) : (
              proposed.map((rule) => (
                <RuleCard key={rule.id} rule={rule} canEdit={canEdit} onEdit={edit} />
              ))
            )}
          </SectionCard>
        </Tabs.Panel>
        <Tabs.Panel value="dispatch">
          <div className="mt-4">
            <DispatchTab data={data} />
          </div>
        </Tabs.Panel>
        <Tabs.Panel value="history">
          <SectionCard padding="md" className="mt-4">
            {tab === "history" ? <HistoryTab /> : null}
          </SectionCard>
        </Tabs.Panel>
      </Tabs.Root>
      <RuleDialog
        key={`${String(editing?.id ?? "new")}-${String(open)}`}
        rule={editing}
        open={open}
        onOpenChange={setOpen}
      />
    </div>
  );
}
